import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import {
  RateReview as RateReviewIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CheckCircleOutline,
} from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useState, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useMsal } from "@azure/msal-react";
import { DataTableShell } from "../components/table/DataTableShell";
import { DocumentCompareDrawer } from "../components/viewer/DocumentCompareDrawer";
import { StatementSwapControl } from "../components/review/StatementSwapControl";
import { UploadInvoiceButton } from "../components/review/UploadInvoiceButton";
import { AiDisclaimer } from "../components/AiDisclaimer";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Reason chip maps (verbatim per UI-SPEC §Review Reason Chip Colors)
// ---------------------------------------------------------------------------

const REASON_LABELS: Record<string, string> = {
  vendor_unknown: "Vendor unknown",
  card_mismatch: "Card mismatch",
  multiple_no_gl: "No GL in invoice",
  invoice_unreadable: "Invoice unreadable",
  missing_gl_account: "No GL resolved",
  ocr_exception: "OCR exception",
  review_queue_policy: "Always review",
  low_confidence_gl: "Low-confidence GL",
};

const REASON_COLORS: Record<
  string,
  "warning" | "info" | "primary" | "error" | "secondary" | "default"
> = {
  vendor_unknown: "warning",
  card_mismatch: "info",
  multiple_no_gl: "primary",
  invoice_unreadable: "error",
  missing_gl_account: "warning",
  ocr_exception: "secondary",
  review_queue_policy: "default",
  low_confidence_gl: "info",
};

const REASON_OPTIONS = [
  { value: "vendor_unknown", label: "Vendor unknown" },
  { value: "card_mismatch", label: "Card mismatch" },
  { value: "multiple_no_gl", label: "No GL in invoice" },
  { value: "invoice_unreadable", label: "Invoice unreadable" },
  { value: "missing_gl_account", label: "No GL resolved" },
  { value: "ocr_exception", label: "OCR exception" },
  { value: "review_queue_policy", label: "Always review" },
  { value: "low_confidence_gl", label: "Low-confidence GL" },
];

// Short hover text shown on the Reason chip (DataGrid tooltip).
const REASON_TOOLTIPS: Record<string, string> = {
  vendor_unknown:
    "This vendor isn't in the catalog yet, so it can't be mapped to a GL account. Add it on the Vendors page — or assign a GL here — and future charges post automatically.",
  card_mismatch:
    "The vendor is mapped to a specific card/account, but this transaction hit a different one. The vendor and GL are known; the account leg is confirmed by a human so it never posts to the wrong account.",
  multiple_no_gl:
    "The matched invoice has multiple line items and no single GL account could be derived. Pick the correct GL to continue.",
  invoice_unreadable:
    "OCR couldn't extract usable data from the invoice (poor scan, unusual format, or a non-invoice file). Re-upload a clearer copy or assign the GL manually.",
  missing_gl_account:
    "Categorization ran but couldn't resolve a GL account number for this transaction. Assign one so it can post.",
  ocr_exception:
    "This vendor is flagged as an OCR exception — its invoices always need a human to confirm the GL before posting.",
  review_queue_policy:
    "This vendor is set to always require human review before posting, regardless of AI confidence.",
  low_confidence_gl:
    "A GL was suggested from a fuzzy match, but confidence is below the 0.95 auto-post threshold. Confirm or correct it before it posts to QuickBooks.",
};

// Longer explanation shown in the Reason detail modal (why + what to do).
const REASON_EXPLANATIONS: Record<string, { why: string; action: string }> = {
  vendor_unknown: {
    why: "The vendor on this transaction has no entry in the vendor catalog, so there is no default GL account (and no default card) to map it to. The system will not guess an expense account on its own — it routes the row here instead.",
    action:
      "Assign a GL account below, or add the vendor on the Vendors page so every future charge from it posts automatically.",
  },
  card_mismatch: {
    why: "This vendor is mapped to a specific card/account in the catalog (its default channel), but the transaction was seen on a different account. The vendor and expense GL are known, yet a vendor appearing on an unexpected account signals that either the payment channel changed or the row was mis-routed — so the account leg is held for confirmation instead of auto-posting to the wrong bank/card. This guard exists because vendors have previously been posted to the wrong account.",
    action:
      "Confirm the account is correct and assign the GL to post, or fix the transaction's account. If the vendor genuinely changed cards, update its default card on the Vendors page.",
  },
  multiple_no_gl: {
    why: "The matched invoice contains multiple line items and the system could not collapse them into a single GL account, so it will not assume one.",
    action: "Review the invoice and pick the GL account that represents the charge.",
  },
  invoice_unreadable: {
    why: "The OCR pipeline could not extract usable structured data from the invoice document — typically a low-quality scan, an unusual layout, or a file that isn't really an invoice.",
    action:
      "Open the source document to read it, re-upload a clearer copy, or assign the GL manually from what you can see.",
  },
  missing_gl_account: {
    why: "Categorization ran for this transaction but finished without resolving a GL account number (the upstream gate could not pick one).",
    action: "Assign a GL account below so the transaction can be posted to QuickBooks.",
  },
  ocr_exception: {
    why: "This vendor is explicitly flagged as an OCR exception in the catalog — its invoices are known to be tricky, so the system never auto-derives their GL and always asks a human.",
    action: "Confirm the GL for this charge. The exception flag stays on the vendor for future invoices.",
  },
  review_queue_policy: {
    why: "This vendor is configured with an always-review policy: every transaction from it is held for a human decision before posting, regardless of how confident the AI is.",
    action:
      "Assign or confirm the GL to release it. To change the policy, edit the vendor on the Vendors page.",
  },
  low_confidence_gl: {
    why: "A GL account was suggested from a fuzzy vendor/invoice match, but the confidence score is below the 0.95 threshold required to auto-post to the client's live QuickBooks. Low confidence is the system asking a human to check before writing real financial data.",
    action:
      "Confirm the suggested GL if it's right, or correct it, then approve. Below 0.95 it will never auto-post on its own.",
  },
};

const ACCOUNT_TYPE_OPTIONS = ["Expense", "Income", "Asset", "Liability"];

// ---------------------------------------------------------------------------
// GL Dropdown Paper with "Create New GL Account" footer
// ---------------------------------------------------------------------------

type GlDropdownPaperProps = React.HTMLAttributes<HTMLDivElement> & {
  onCreateGl: () => void;
  isAdmin: boolean;
};

function GlDropdownPaper({ children, onCreateGl, isAdmin, ...rest }: GlDropdownPaperProps) {
  return (
    <Paper {...rest}>
      {children}
      <Divider />
      <Box sx={{ p: 1 }}>
        <Tooltip
          title={!isAdmin ? "Creating GL Accounts requires admin access." : ""}
          placement="bottom-start"
        >
          <span>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onMouseDown={(e) => {
                e.preventDefault();
                if (isAdmin) onCreateGl();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "var(--c-link-accent)",
              }}
              disabled={!isAdmin}
            >
              + Create New GL Account
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ReviewQueue() {
  // --- MSAL identity (verbatim from AppShell.tsx lines 110-137) ---
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";

  // Admin role check — raw useQuery with "skip" semantics per AppShell pattern
  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const isAdmin = userRecord?.role === "admin";

  // --- Data queries (MANDATORY useStableQuery, never raw useQuery) ---
  // Client request 2026-10-16: every row in review, not the first 500. The
  // query already reads the whole review bucket; the limit only cut the reply.
  const txnsRaw = useStableQuery(api.transactions.listReviewQueue, { limit: 100000 }, []);
  const glAccountsRaw = useStableQuery(api.glAccounts.listActive, {}, []);
  const cardsRaw = useStableQuery(api.cards.listActive, {}, []);

  const loading = txnsRaw === undefined;
  const txns = txnsRaw ?? [];
  // Client rule 2026-06-03: 0000 (income) and 0001 (outcome) are both real,
  // postable catch-all accounts now — show all active GLs in the picker.
  const glAccounts = glAccountsRaw ?? [];
  const cards = cardsRaw ?? [];

  // --- Mutations ---
  const approveFromReview = useMutation(api.transactions.approveFromReview);
  const createFromReview = useMutation(api.glAccounts.createFromReview);

  // --- Filter state ---
  const [filterReasons, setFilterReasons] = useState<string[]>([]);
  const [filterCards, setFilterCards] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const hasFilters =
    filterReasons.length > 0 ||
    filterCards.length > 0 ||
    !!filterDateFrom ||
    !!filterDateTo;

  const clearFilters = useCallback(() => {
    setFilterReasons([]);
    setFilterCards([]);
    setFilterDateFrom("");
    setFilterDateTo("");
  }, []);

  // --- Client-side filtering (mirrors MatchReview filteredMatches pattern) ---
  const filteredTxns = useMemo(() => {
    let rows = txns;
    if (filterReasons.length > 0) {
      rows = rows.filter((t: any) => filterReasons.includes(t.vendorGlReviewReason));
    }
    if (filterCards.length > 0) {
      rows = rows.filter((t: any) => filterCards.includes(t.bankAccountNumber));
    }
    if (filterDateFrom) {
      rows = rows.filter((t: any) => t.date >= filterDateFrom);
    }
    if (filterDateTo) {
      rows = rows.filter((t: any) => t.date <= filterDateTo);
    }
    return rows;
  }, [txns, filterReasons, filterCards, filterDateFrom, filterDateTo]);

  // --- Review popup state ---
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState<any>(null);
  // Source-document viewer (same experience as /transactions): clicking a row
  // — or the popup button — opens the originating bank/invoice document so the
  // human can read it while assigning GL. Keyed on its own txn so a row click
  // works independently of the GL-assignment popup.
  const [docViewerTxn, setDocViewerTxn] = useState<any>(null);
  // Reason-detail modal: clicking a Reason chip opens a plain-English
  // explanation of why THIS row landed in the review queue.
  const [reasonInfoTxn, setReasonInfoTxn] = useState<any>(null);
  const reviewDocs = useQuery(
    api.transactions.getDocumentsForCompare,
    docViewerTxn?._id ? { id: docViewerTxn._id as Id<"transactions"> } : "skip",
  );
  // Matched invoice of the transaction open in the GL popup (shown inline,
  // next to the "Upload invoice" action — client request 5).
  const popupDocs = useQuery(
    api.transactions.getDocumentsForCompare,
    popupOpen && selectedTxn?._id ? { id: selectedTxn._id as Id<"transactions"> } : "skip",
  );
  const [selectedGl, setSelectedGl] = useState<any>(null);
  const [description, setDescription] = useState("");
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // --- Create-GL sub-form state ---
  const [createGlOpen, setCreateGlOpen] = useState(false);
  const [glNumber, setGlNumber] = useState("");
  const [glDescription, setGlDescription] = useState("");
  const [glAccountType, setGlAccountType] = useState("Expense");
  const [creatingGl, setCreatingGl] = useState(false);

  // --- Snackbar state ---
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState<"success" | "error">("success");

  const showSnack = useCallback((message: string, severity: "success" | "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  }, []);

  // --- Popup open/close ---
  const openPopup = useCallback((row: any) => {
    setSelectedTxn(row);
    setSelectedGl(null);
    setDescription(row.description ?? "");
    setDescriptionEditing(false);
    setSubmitting(false);
    setPopupOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setPopupOpen(false);
    setSelectedTxn(null);
    setSelectedGl(null);
    setDescription("");
    setDescriptionEditing(false);
  }, [submitting]);

  // --- GL selection handler ---
  const handleGlSelect = useCallback((_event: any, gl: any) => {
    setSelectedGl(gl);
    if (!descriptionEditing) {
      setDescription(gl?.description ?? "");
    }
  }, [descriptionEditing]);

  // --- Confirm handler ---
  const handleConfirm = useCallback(async () => {
    if (!selectedGl || !selectedTxn) return;
    setSubmitting(true);
    try {
      const result = await approveFromReview({
        transactionId: selectedTxn._id as Id<"transactions">,
        glAccountNumber: selectedGl.number,
        glAccountId: selectedGl._id as Id<"glAccounts">,
        description,
        reviewerEmail: email,
      });
      // GL not yet in QuickBooks: the backend just kicked a high-priority
      // AccountAdd sync. Keep the popup open with the GL selected so a single
      // retry (after the next QBWC poll) completes the approval.
      // (A `vendor_syncing` branch used to live here too, but approveFromReview
      // only ever returns "gl_syncing" or "approved" — the vendor auto-create
      // resolves before the Step-1 patch, so that status is never emitted. The
      // dead comparison also broke the build under TS. 2026-07-27.)
      if (result?.status === "gl_syncing") {
        setSubmitting(false);
        showSnack(
          result.message ??
            `GL ${selectedGl.number} is being created in QuickBooks — wait a moment and click Confirm again.`,
          "error",
        );
        return;
      }
      setPopupOpen(false);
      setSelectedTxn(null);
      setSelectedGl(null);
      setDescription("");
      setDescriptionEditing(false);
      showSnack("Transaction approved — queued for QuickBooks", "success");
    } catch (err) {
      setSubmitting(false);
      // Surface the REAL server message (e.g. "GL 5600 is being created in
      // QuickBooks — try again in a few minutes") instead of a generic
      // "try again". Convex wraps the thrown Error; strip its prefix so the
      // accountant sees the actionable sentence. (2026-06-18: a freshly
      // created GL whose AccountAdd had not yet synced to QBD failed here
      // with the cause hidden behind the generic message.)
      const raw = err instanceof Error ? err.message : String(err);
      const m = raw.match(/Uncaught Error:\s*(.*?)(?:\n|\s+at\s|$)/);
      const friendly = (m?.[1] ?? raw).trim() || "Failed to submit review. Please try again.";
      showSnack(friendly, "error");
    }
  }, [selectedGl, selectedTxn, description, email, approveFromReview, showSnack]);

  // --- Create GL sub-form ---
  const openCreateGlSubForm = useCallback(() => {
    setGlNumber("");
    setGlDescription("");
    setGlAccountType("Expense");
    setCreatingGl(false);
    setCreateGlOpen(true);
  }, []);

  const cancelCreateGl = useCallback(() => {
    setCreateGlOpen(false);
  }, []);

  const handleCreateGl = useCallback(async () => {
    if (!/^\d{4}$/.test(glNumber) || !glDescription.trim()) return;
    setCreatingGl(true);
    try {
      await createFromReview({
        number: glNumber,
        description: glDescription.trim(),
        accountType: glAccountType,
        reviewerEmail: email,
      });
      setCreateGlOpen(false);
      // Auto-select the newly created GL once Convex reactivity surfaces it
      // We use a stub to immediately pre-select for responsiveness; the reactive
      // update will replace it with the real object containing _id.
      const newGlStub = { number: glNumber, description: glDescription.trim(), accountType: glAccountType };
      setSelectedGl(newGlStub);
      if (!descriptionEditing) {
        setDescription(glDescription.trim());
      }
    } catch (err: any) {
      setCreatingGl(false);
      const msg = err?.message ?? "Failed to create GL Account. Please try again.";
      showSnack(msg, "error");
    }
  }, [glNumber, glDescription, glAccountType, email, createFromReview, descriptionEditing, showSnack]);

  // --- DataGrid columns ---
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "date",
        headerName: "Date",
        width: 110,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">{formatDate(params.value as string)}</Typography>
        ),
      },
      {
        field: "vendorName",
        headerName: "Vendor",
        flex: 1.5,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            title={params.value as string}
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {params.value as string}
          </Typography>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 120,
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(params.value as number)}
          </Typography>
        ),
      },
      {
        // The account/card the transaction hit lives on `bankAccountNumber`
        // (e.g. "0517", "9183"); the row has no `card` field — reading it left
        // this column blank for every row. 2026-07-27 fix.
        field: "bankAccountNumber",
        headerName: "Card",
        width: 110,
        renderCell: (params: GridRenderCellParams) => {
          const val = params.value as string | undefined;
          return val ? (
            <Chip size="small" label={val} variant="outlined" />
          ) : (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              —
            </Typography>
          );
        },
      },
      {
        field: "vendorGlReviewReason",
        headerName: "Reason",
        width: 190,
        renderCell: (params: GridRenderCellParams) => {
          const reason = params.value as string;
          return (
            <Tooltip
              arrow
              title={
                REASON_TOOLTIPS[reason] ??
                "This transaction needs a human GL decision before it can post to QuickBooks."
              }
            >
              <Chip
                size="small"
                color={REASON_COLORS[reason] ?? "default"}
                label={REASON_LABELS[reason] ?? reason}
                onClick={(e) => {
                  e.stopPropagation();
                  setReasonInfoTxn(params.row);
                }}
                sx={{ cursor: "pointer" }}
                aria-label={`Reason: ${REASON_LABELS[reason] ?? reason}. Click for details on why this is in review.`}
              />
            </Tooltip>
          );
        },
      },
      {
        field: "_id",
        headerName: "",
        width: 60,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <IconButton
            size="small"
            aria-label="Review and approve transaction"
            onClick={(e) => {
              e.stopPropagation();
              openPopup(params.row);
            }}
          >
            <RateReviewIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [openPopup],
  );

  // --- Filter bar (passed as slot to DataTableShell) ---
  const filterBar = (
    <>
      <Autocomplete
        multiple
        size="small"
        options={REASON_OPTIONS}
        getOptionLabel={(opt) => opt.label}
        value={REASON_OPTIONS.filter((o) => filterReasons.includes(o.value))}
        onChange={(_e, newVal) => setFilterReasons(newVal.map((o) => o.value))}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              size="small"
              color={REASON_COLORS[option.value] ?? "default"}
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Filter by reason" sx={{ minWidth: 200 }} />
        )}
        sx={{ minWidth: 200 }}
      />
      <Autocomplete
        multiple
        size="small"
        options={cards}
        getOptionLabel={(c: any) => `${c.number} — ${c.description ?? c.number}`}
        value={cards.filter((c: any) => filterCards.includes(c.number))}
        onChange={(_e, newVal) => setFilterCards((newVal as any[]).map((c) => c.number))}
        renderInput={(params) => (
          <TextField {...params} label="Filter by card" sx={{ minWidth: 200 }} />
        )}
        sx={{ minWidth: 200 }}
      />
      <Stack direction="row" spacing={1}>
        <TextField
          type="date"
          size="small"
          label="From date"
          InputLabelProps={{ shrink: true }}
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          sx={{ width: 150 }}
        />
        <TextField
          type="date"
          size="small"
          label="To date"
          InputLabelProps={{ shrink: true }}
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          sx={{ width: 150 }}
        />
      </Stack>
      {hasFilters && (
        <Button variant="outlined" size="small" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </>
  );

  return (
    <Box>
      <AiDisclaimer context="These transactions need a GL account; the AI's suggestions can be wrong" />
      <DataTableShell
        data-tour="page-review-queue"
        title="Review Queue"
        subtitle="Transactions needing GL assignment before QuickBooks posting"
        rows={filteredTxns}
        columns={columns}
        loading={loading}
        totalBeforeFilters={txns.length}
        filterBar={filterBar}
        onResetFilters={hasFilters ? clearFilters : undefined}
        itemLabel="transaction"
        emptyTitle="Review queue is clear"
        emptyDescription="All transactions have been categorized. Nothing awaiting review."
        emptyIcon={<CheckCircleOutline sx={{ fontSize: 32, color: "var(--c-metric-success)" }} />}
        gridProps={{
          initialState: {
            sorting: { sortModel: [{ field: "date", sort: "desc" }] },
            pagination: { paginationModel: { pageSize: 25 } },
          },
          disableRowSelectionOnClick: true,
          autoHeight: true,
          // Click a row to view its source document (same UX as /transactions).
          onRowClick: (params) => setDocViewerTxn(params.row),
          sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
        }}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Review popup Dialog                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={popupOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="review-dialog-title"
      >
        <DialogTitle id="review-dialog-title" sx={{ pr: 6 }}>
          Review Transaction
          <IconButton
            aria-label="Close review dialog"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedTxn && (
            <>
              {/* Section A — Transaction Context (read-only) */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "rgba(108,92,231,0.06)",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--c-link-accent)",
                    mb: 1,
                  }}
                >
                  Transaction Details
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Date:</strong> {formatDate(selectedTxn.date)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Vendor:</strong> {selectedTxn.vendorName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    <strong style={{ fontWeight: 400 }}>Amount:</strong>{" "}
                    {formatCurrency(selectedTxn.amount)}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                      <strong>Card:</strong>
                    </Typography>
                    <Chip size="small" label={selectedTxn.bankAccountNumber ?? "—"} variant="outlined" />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                      <strong>Reason:</strong>
                    </Typography>
                    <Chip
                      size="small"
                      color={REASON_COLORS[selectedTxn.vendorGlReviewReason] ?? "default"}
                      label={REASON_LABELS[selectedTxn.vendorGlReviewReason] ?? selectedTxn.vendorGlReviewReason}
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Typography variant="body2">
                      <strong>Invoice:</strong>
                    </Typography>
                    {popupDocs === undefined ? (
                      <CircularProgress size={12} />
                    ) : popupDocs?.invoiceId ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={
                          popupDocs.matchStatus === "pending_review" ? "warning" : "success"
                        }
                        label={
                          popupDocs.invoiceFileName ??
                          `${popupDocs.invoiceVendorName ?? "Invoice"}${popupDocs.invoiceNumber ? ` #${popupDocs.invoiceNumber}` : ""}`
                        }
                        onClick={
                          popupDocs.invoiceStorageId
                            ? () => setDocViewerTxn(selectedTxn)
                            : undefined
                        }
                      />
                    ) : (
                      <Chip size="small" color="warning" label="Missing" />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDocViewerTxn(selectedTxn)}
                    >
                      View source document
                    </Button>
                    <UploadInvoiceButton
                      transactionId={selectedTxn._id}
                      actorEmail={email}
                      hasInvoice={Boolean(popupDocs?.invoiceId)}
                      onResult={showSnack}
                    />
                  </Stack>
                </Stack>
              </Box>

              {/* Section B — GL Account Autocomplete */}
              <Autocomplete
                options={glAccounts}
                getOptionLabel={(gl: any) => `${gl.number} — ${gl.description}`}
                value={selectedGl}
                onChange={handleGlSelect}
                isOptionEqualToValue={(a: any, b: any) =>
                  a._id === b._id || a.number === b.number
                }
                groupBy={(gl: any) => gl.accountType ?? ""}
                PaperComponent={(paperProps: React.HTMLAttributes<HTMLDivElement>) => (
                  <GlDropdownPaper
                    {...paperProps}
                    onCreateGl={openCreateGlSubForm}
                    isAdmin={isAdmin}
                  />
                )}
                renderInput={(params) => (
                  <TextField {...params} label="GL Account" size="small" fullWidth />
                )}
                fullWidth
              />

              {/* Section C — Description Field */}
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                InputProps={{
                  readOnly: !descriptionEditing,
                  endAdornment: !descriptionEditing ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Edit description"
                        onClick={() => setDescriptionEditing(true)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
                helperText={
                  descriptionEditing
                    ? "Editing the description is not recommended. This value has been stable in production for years."
                    : undefined
                }
                FormHelperTextProps={{ sx: { color: "var(--c-metric-warning)" } }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleClose} disabled={submitting}>
            Keep in queue
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedGl || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? "Submitting..." : "Confirm & Send to QuickBooks"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Source-document viewer — side-by-side statement + related invoice
          (same experience as /transactions), so the reviewer can see WHICH
          invoice the transaction belongs to while assigning the GL.
          2026-06-12 — request from the client workflow: the single-document
          preview showed only the statement and she could not tell which
          invoice the row referred to. */}
      <DocumentCompareDrawer
        open={!!docViewerTxn}
        onClose={() => setDocViewerTxn(null)}
        title="Document Comparison"
        subtitle={
          docViewerTxn
            ? `${docViewerTxn.vendorName ?? docViewerTxn.description ?? ""} — ${formatDate(docViewerTxn.date)} · ${formatCurrency(docViewerTxn.amount)}`
            : undefined
        }
        left={{
          storageId: (reviewDocs?.bankStorageId as string | null | undefined) ?? null,
          label: reviewDocs?.bankFileName
            ? `Bank Statement · ${reviewDocs.bankFileName}`
            : "Bank Statement",
        }}
        right={{
          storageId: (reviewDocs?.invoiceStorageId as string | null | undefined) ?? null,
          label: reviewDocs?.invoiceVendorName
            ? `Invoice · ${reviewDocs.invoiceVendorName}${reviewDocs.invoiceNumber ? ` #${reviewDocs.invoiceNumber}` : ""}`
            : "Invoice",
        }}
        editTarget={
          docViewerTxn?._id ? { type: "transaction", id: docViewerTxn._id } : null
        }
        leftAction={
          <StatementSwapControl
            transaction={
              reviewDocs?.transaction
                ? {
                    _id: reviewDocs.transaction._id,
                    sourceFileId: reviewDocs.transaction.sourceFileId ?? null,
                    postingStatus: reviewDocs.transaction.postingStatus,
                    qbImportStatus: reviewDocs.transaction.qbImportStatus,
                  }
                : (docViewerTxn ? { _id: docViewerTxn._id, sourceFileId: docViewerTxn.sourceFileId ?? null, postingStatus: docViewerTxn.postingStatus, qbImportStatus: docViewerTxn.qbImportStatus } : null)
            }
            currentFileName={reviewDocs?.bankFileName ?? null}
            onSwapped={() => showSnack("Statement swapped — reprocessing", "success")}
          />
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* Reason detail modal — "why is this in the review queue?"            */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={!!reasonInfoTxn}
        onClose={() => setReasonInfoTxn(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="reason-info-title"
      >
        {reasonInfoTxn &&
          (() => {
            const reason = reasonInfoTxn.vendorGlReviewReason as string;
            const info = REASON_EXPLANATIONS[reason];
            return (
              <>
                <DialogTitle id="reason-info-title" sx={{ pr: 6 }}>
                  Why is this in the Review Queue?
                  <IconButton
                    aria-label="Close reason details"
                    onClick={() => setReasonInfoTxn(null)}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  <Stack spacing={2}>
                    <Chip
                      size="small"
                      color={REASON_COLORS[reason] ?? "default"}
                      label={REASON_LABELS[reason] ?? reason}
                      sx={{ alignSelf: "flex-start" }}
                    />
                    <Box
                      sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(108,92,231,0.06)" }}
                    >
                      <Stack spacing={0.25}>
                        <Typography variant="body2">
                          <strong>Vendor:</strong> {reasonInfoTxn.vendorName ?? "—"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Date:</strong> {formatDate(reasonInfoTxn.date)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Amount:</strong> {formatCurrency(reasonInfoTxn.amount)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Card / account:</strong>{" "}
                          {reasonInfoTxn.bankAccountNumber ?? "—"}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Why it needs review
                      </Typography>
                      <Typography variant="body2">
                        {info?.why ??
                          "This transaction needs a human GL decision before it can post to QuickBooks."}
                      </Typography>
                    </Box>
                    {info?.action && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          What to do
                        </Typography>
                        <Typography variant="body2">{info.action}</Typography>
                      </Box>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button variant="outlined" onClick={() => setReasonInfoTxn(null)}>
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const row = reasonInfoTxn;
                      setReasonInfoTxn(null);
                      openPopup(row);
                    }}
                  >
                    Assign GL
                  </Button>
                </DialogActions>
              </>
            );
          })()}
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Create-GL sub-form Dialog (stacked)                                 */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={createGlOpen}
        onClose={cancelCreateGl}
        maxWidth="xs"
        fullWidth
        aria-labelledby="create-gl-dialog-title"
      >
        <DialogTitle id="create-gl-dialog-title">New GL Account</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="GL Number"
              size="small"
              fullWidth
              value={glNumber}
              onChange={(e) => setGlNumber(e.target.value)}
              placeholder="e.g. 5200"
              error={glNumber.length > 0 && !/^\d{4}$/.test(glNumber)}
              helperText={
                glNumber.length > 0 && !/^\d{4}$/.test(glNumber)
                  ? "Must be a 4-digit number"
                  : undefined
              }
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              value={glDescription}
              onChange={(e) => setGlDescription(e.target.value)}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={glAccountType}
                label="Account Type"
                onChange={(e) => setGlAccountType(e.target.value)}
              >
                {ACCOUNT_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={cancelCreateGl} disabled={creatingGl}>
            Back to review
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGl}
            disabled={!/^\d{4}$/.test(glNumber) || !glDescription.trim() || creatingGl}
            startIcon={
              creatingGl ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {creatingGl ? "Creating..." : "Create Account"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar (bottom-center, 3s)                                        */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          severity={snackSeverity}
          onClose={() => setSnackOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ReviewQueue;
